import {
    AbstractControl,
    AsyncValidatorFn,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { AnchorServiceLike, ModalFieldConfig, ModalProps } from './modal-field.interface';
import { BaseAnchor, BaseVersion } from 'src/models/anchor-and-version/base.model';
import { CUSTOM_ELEMENTS_SCHEMA, Component, Input, OnDestroy, inject } from '@angular/core';
import {
    Observable,
    Subject,
    debounceTime,
    distinctUntilChanged,
    first,
    map,
    of,
    switchMap,
} from 'rxjs';
import {
    applyDefaultValue,
    createServiceConfig,
    getFieldValue,
    getLatestVersion,
    markFormGroupTouched,
    parseHexFieldValue,
    processFormData,
} from './utils/form.utils';
import { AddressAnchor } from 'src/models/anchor-and-version/address.model';
import { AddressService } from 'src/services/anchor-and-version-services/address.service';
import { AllocationElementAnchor } from 'src/models/anchor-and-version/allocation-element.model';
import { AllocationElementService } from 'src/services/anchor-and-version-services/allocation-element.service';
import { AnchorAndVersionBaseFilter } from 'src/filters/anchor-and-version-filter/anchor-and-version-base.filter';
import { CanComponentDeactivate } from 'src/guards/can-deactivate.guard';
import { ChangeRequest } from 'src/models/change-request-dto/change-request.dto';
import { ChangeRequestService } from 'src/services/basic-services/change-request.service';
import { ComponentAnchor } from 'src/models/anchor-and-version/component.model';
import { ComponentCodeAnchor } from 'src/models/anchor-and-version/component-code.model';
import { ComponentCodeService } from 'src/services/anchor-and-version-services/component-code.service';
import { ComponentService } from 'src/services/anchor-and-version-services/component.service';
import { ConfigState } from 'src/store/reducers/config.reducer';
import { ConfirmationDialogService } from 'src/services/confirmation-dialog.service';
import { DropdownLazyComponent } from '../../utils/dropdown-lazy/dropdown-lazy.component';
import { FIELD_TO_SERVICE_MAP } from './utils/dropdown-service.utils';
import { FunctionCategoryAnchor } from 'src/models/anchor-and-version/function-category.model';
import { FunctionCategoryService } from 'src/services/anchor-and-version-services/function-category.service';
import { FvBoxPortDataRelationAnchor } from 'src/models/anchor-and-version/fv-box-port-data-relation.model';
import { FvBoxPortDataRelationService } from 'src/services/anchor-and-version-services/fv-box-can-port-data-relation.service';
import { NotificationService } from 'src/services/notification.service';
import { PendingChangesService } from 'src/services/pending-changes-service/pending-changes.service';
import { SegmentAnchor } from 'src/models/anchor-and-version/segment.model';
import { SegmentService } from 'src/services/anchor-and-version-services/segment.service';
import { SignalAnchor } from 'src/models/anchor-and-version/signal.model';
import { SignalService } from 'src/services/anchor-and-version-services/signal.service';
import { Store } from '@ngrx/store';
import { UseCaseAnchor } from 'src/models/anchor-and-version/use-case.model';
import { UseCaseService } from 'src/services/anchor-and-version-services/use-case.service';
import { UserFunctionAnchor } from 'src/models/anchor-and-version/user-function.model';
import { UserFunctionService } from 'src/services/anchor-and-version-services/user-function.service';
import { ValueDescriptionAnchor } from 'src/models/anchor-and-version/value-description.model';
import { ValueDescriptionService } from 'src/services/anchor-and-version-services/value-description.service';
import { nameFormatValidator } from 'src/validators/name-format.validator';
import { refreshPendingChangesCount } from 'src/utils/pending-changes-count.util';
import { takeUntil } from 'rxjs/operators';
import { trimmedRequiredValidator } from 'src/validators/required.validator';

// PrimeNG
// eslint-disable-next-line sort-imports
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';

interface DropdownConfig {
    optionLabel: string;
    isLoading: boolean;
    options: unknown[];
    hasMore: boolean;
    onLoadMore: (str: string) => void;
    onSearch: (str: string) => void;
}

@Component({
    selector: 'app-crud-base-modal',
    templateUrl: './crud-base-modal.component.html',
    styleUrls: ['./crud-base-modal.component.scss'],
    imports: [
        FormsModule,
        ReactiveFormsModule,
        ButtonModule,
        DialogModule,
        SelectModule,
        ToggleSwitchModule,
        InputTextModule,
        TextareaModule,
        InputMaskModule,
        DropdownLazyComponent,
        ToastModule,
        TooltipModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CrudBaseModalComponent<T extends BaseAnchor<V>, V extends BaseVersion>
    implements OnDestroy, CanComponentDeactivate
{
    @Input() public modalProps!: ModalProps;

    public addresses: AddressAnchor[] = [];
    public addressesHasMore = true;
    public addressesIsLoading = false;
    public addressesLimit = 30;
    public allocationElements: AllocationElementAnchor[] = [];
    public allocationElementsHasMore = true;
    public allocationElementsIsLoading = false;
    public allocationElementsLimit = 30;
    public changeRequests: ChangeRequest[] = [];
    public changeRequestsHasMore = true;
    public changeRequestsIsLoading = false;
    public changeRequestsLimit = 30;
    public componentCodes: ComponentCodeAnchor[] = [];
    public componentCodesHasMore = true;
    public componentCodesIsLoading = false;
    public componentCodesLimit = 30;
    public components: ComponentAnchor[] = [];
    public componentsHasMore = true;
    public componentsIsLoading = false;
    public componentsLimit = 30;
    public form!: FormGroup;
    public functionCategories: FunctionCategoryAnchor[] = [];
    public functionCategoriesHasMore = true;
    public functionCategoriesIsLoading = false;
    public functionCategoriesLimit = 30;
    public fvBoxPortDataRelations: FvBoxPortDataRelationAnchor[] = [];
    public fvBoxPortDataRelationsHasMore = true;
    public fvBoxPortDataRelationsIsLoading = false;
    public fvBoxPortDataRelationsLimit = 30;
    public hasUnsavedChanges = false;
    public isCreating = false;
    public isModalVisible = false;
    public segments: SegmentAnchor[] = [];
    public segmentsHasMore = true;
    public segmentsIsLoading = false;
    public segmentsLimit = 30;
    public signals: SignalAnchor[] = [];
    public signalsHasMore = true;
    public signalsIsLoading = false;
    public signalsLimit = 30;
    public useCases: UseCaseAnchor[] = [];
    public useCasesHasMore = true;
    public useCasesIsLoading = false;
    public useCasesLimit = 30;
    public userFunctions: UserFunctionAnchor[] = [];
    public userFunctionsHasMore = true;
    public userFunctionsIsLoading = false;
    public userFunctionsLimit = 30;
    public valueDescriptions: ValueDescriptionAnchor[] = [];
    public valueDescriptionsHasMore = true;
    public valueDescriptionsIsLoading = false;
    public valueDescriptionsLimit = 30;

    protected addressService = inject(AddressService);
    protected allocationElementService = inject(AllocationElementService);
    protected componentCodeService = inject(ComponentCodeService);
    protected componentService = inject(ComponentService);
    protected confirmationDialogService = inject(ConfirmationDialogService);
    protected crService = inject(ChangeRequestService);
    protected destroy$ = new Subject<void>();
    protected functionCategoryService = inject(FunctionCategoryService);
    protected fvBoxPortDataRelationService = inject(FvBoxPortDataRelationService);
    protected notificationService = inject(NotificationService);
    protected pendingChangesService = inject(PendingChangesService);
    protected segmentService = inject(SegmentService);
    protected signalService = inject(SignalService);
    protected store = inject<Store<{ config: ConfigState }>>(Store);
    protected useCaseService = inject(UseCaseService);
    protected userFunctionService = inject(UserFunctionService);
    protected valueDescriptionService = inject(ValueDescriptionService);

    private readonly dropdownLoaders = {
        primaryAddressAnchorKey: () => this.handleAnchorServiceOperation('addresses', ''),
        componentAnchorKey: () => this.handleAnchorServiceOperation('components', ''),
        userFunctionAnchorKey: () => this.handleAnchorServiceOperation('userFunctions', ''),
        componentCodeAnchorKey: () => this.handleAnchorServiceOperation('componentCodes', ''),
        useCaseAnchorKey: () => this.handleAnchorServiceOperation('useCases', ''),
        functionCategoryAnchorKey: () =>
            this.handleAnchorServiceOperation('functionCategories', ''),
        segmentAnchorKey: () => this.handleAnchorServiceOperation('segments', ''),
        signalAnchorKey: () => this.handleAnchorServiceOperation('signals', ''),
        sourceAeAnchorKey: () => this.handleAnchorServiceOperation('allocationElements', ''),
        targetAeAnchorKey: () => this.handleAnchorServiceOperation('allocationElements', ''),
        fvBoxPortDataRelationAnchorKey: () =>
            this.handleAnchorServiceOperation('fvBoxPortDataRelations', ''),
        valueDescriptionAnchorKey: () => this.handleAnchorServiceOperation('valueDescriptions', ''),
    };

    private readonly propertyMap = new Map([
        [
            'addresses',
            {
                array: () => this.addresses,
                setArray: (v: unknown[]) => (this.addresses = v as AddressAnchor[]),
                setLoading: (v: boolean) => (this.addressesIsLoading = v),
                setHasMore: (v: boolean) => (this.addressesHasMore = v),
                getLimit: () => this.addressesLimit,
            },
        ],
        [
            'componentCodes',
            {
                array: () => this.componentCodes,
                setArray: (v: unknown[]) => (this.componentCodes = v as ComponentCodeAnchor[]),
                setLoading: (v: boolean) => (this.componentCodesIsLoading = v),
                setHasMore: (v: boolean) => (this.componentCodesHasMore = v),
                getLimit: () => this.componentCodesLimit,
            },
        ],
        [
            'components',
            {
                array: () => this.components,
                setArray: (v: unknown[]) => (this.components = v as ComponentAnchor[]),
                setLoading: (v: boolean) => (this.componentsIsLoading = v),
                setHasMore: (v: boolean) => (this.componentsHasMore = v),
                getLimit: () => this.componentsLimit,
            },
        ],
        [
            'functionCategories',
            {
                array: () => this.functionCategories,
                setArray: (v: unknown[]) =>
                    (this.functionCategories = v as FunctionCategoryAnchor[]),
                setLoading: (v: boolean) => (this.functionCategoriesIsLoading = v),
                setHasMore: (v: boolean) => (this.functionCategoriesHasMore = v),
                getLimit: () => this.functionCategoriesLimit,
            },
        ],
        [
            'useCases',
            {
                array: () => this.useCases,
                setArray: (v: unknown[]) => (this.useCases = v as UseCaseAnchor[]),
                setLoading: (v: boolean) => (this.useCasesIsLoading = v),
                setHasMore: (v: boolean) => (this.useCasesHasMore = v),
                getLimit: () => this.useCasesLimit,
            },
        ],
        [
            'segments',
            {
                array: () => this.segments,
                setArray: (v: unknown[]) => (this.segments = v as SegmentAnchor[]),
                setLoading: (v: boolean) => (this.segmentsIsLoading = v),
                setHasMore: (v: boolean) => (this.segmentsHasMore = v),
                getLimit: () => this.segmentsLimit,
            },
        ],
        [
            'allocationElements',
            {
                array: () => this.allocationElements,
                setArray: (v: unknown[]) =>
                    (this.allocationElements = v as AllocationElementAnchor[]),
                setLoading: (v: boolean) => (this.allocationElementsIsLoading = v),
                setHasMore: (v: boolean) => (this.allocationElementsHasMore = v),
                getLimit: () => this.allocationElementsLimit,
            },
        ],
        [
            'fvBoxPortDataRelations',
            {
                array: () => this.fvBoxPortDataRelations,
                setArray: (v: unknown[]) =>
                    (this.fvBoxPortDataRelations = v as FvBoxPortDataRelationAnchor[]),
                setLoading: (v: boolean) => (this.fvBoxPortDataRelationsIsLoading = v),
                setHasMore: (v: boolean) => (this.fvBoxPortDataRelationsHasMore = v),
                getLimit: () => this.fvBoxPortDataRelationsLimit,
            },
        ],
        [
            'signals',
            {
                array: () => this.signals,
                setArray: (v: unknown[]) => (this.signals = v as SignalAnchor[]),
                setLoading: (v: boolean) => (this.signalsIsLoading = v),
                setHasMore: (v: boolean) => (this.signalsHasMore = v),
                getLimit: () => this.signalsLimit,
            },
        ],
        [
            'valueDescriptions',
            {
                array: () => this.valueDescriptions,
                setArray: (v: unknown[]) =>
                    (this.valueDescriptions = v as ValueDescriptionAnchor[]),
                setLoading: (v: boolean) => (this.valueDescriptionsIsLoading = v),
                setHasMore: (v: boolean) => (this.valueDescriptionsHasMore = v),
                getLimit: () => this.valueDescriptionsLimit,
            },
        ],
        [
            'userFunctions',
            {
                array: () => this.userFunctions,
                setArray: (v: unknown[]) => (this.userFunctions = v as UserFunctionAnchor[]),
                setLoading: (v: boolean) => (this.userFunctionsIsLoading = v),
                setHasMore: (v: boolean) => (this.userFunctionsHasMore = v),
                getLimit: () => this.userFunctionsLimit,
            },
        ],
    ]);

    private get serviceConfig() {
        return createServiceConfig(this as unknown as Record<string, unknown>);
    }

    public canDeactivate(): Observable<boolean> | boolean {
        if (this.hasUnsavedChanges && this.isModalVisible) {
            return this.confirmationDialogService.confirmUnsavedChanges();
        }
        return true;
    }

    public getAllFields(): ModalFieldConfig[] {
        return [
            ...(this.modalProps?.anchorFields || []),
            ...(this.modalProps?.versionFields || []),
        ].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    }

    public getDropdownConfig(fieldName: string): DropdownConfig {
        const configs: Record<string, DropdownConfig> = {
            primaryAddressAnchorKey: {
                optionLabel: 'displayName',
                isLoading: this.addressesIsLoading,
                options: this.addresses,
                hasMore: this.addressesHasMore,
                onLoadMore: (str: string) => this.onLoadMoreAddresses(str),
                onSearch: (str: string) => this.onSearchAddresses(str),
            },
            changeRequestKey: {
                optionLabel: 'name',
                isLoading: this.changeRequestsIsLoading,
                options: this.changeRequests,
                hasMore: this.changeRequestsHasMore,
                onLoadMore: (str: string) => this.onLoadMoreChangeRequests(str),
                onSearch: (str: string) => this.onSearchChangeRequests(str),
            },
            componentAnchorKey: {
                optionLabel: 'displayName',
                isLoading: this.componentsIsLoading,
                options: this.components,
                hasMore: this.componentsHasMore,
                onLoadMore: (str: string) => this.onLoadMoreComponents(str),
                onSearch: (str: string) => this.onSearchComponents(str),
            },
            userFunctionAnchorKey: {
                optionLabel: 'displayName',
                isLoading: this.userFunctionsIsLoading,
                options: this.userFunctions,
                hasMore: this.userFunctionsHasMore,
                onLoadMore: (str: string) => this.onLoadMoreUserFunctions(str),
                onSearch: (str: string) => this.onSearchUserFunctions(str),
            },
            componentCodeAnchorKey: {
                optionLabel: 'displayName',
                isLoading: this.componentCodesIsLoading,
                options: this.componentCodes,
                hasMore: this.componentCodesHasMore,
                onLoadMore: (str: string) => this.onLoadMoreComponentCodes(str),
                onSearch: (str: string) => this.onSearchComponentCodes(str),
            },
            useCaseAnchorKey: {
                optionLabel: 'displayName',
                isLoading: this.useCasesIsLoading,
                options: this.useCases,
                hasMore: this.useCasesHasMore,
                onLoadMore: (str: string) => this.onLoadMoreUseCases(str),
                onSearch: (str: string) => this.onSearchUseCases(str),
            },
            functionCategoryAnchorKey: {
                optionLabel: 'displayName',
                isLoading: this.functionCategoriesIsLoading,
                options: this.functionCategories,
                hasMore: this.functionCategoriesHasMore,
                onLoadMore: (str: string) => this.onLoadMoreFunctionCategories(str),
                onSearch: (str: string) => this.onSearchFunctionCategories(str),
            },
            segmentAnchorKey: {
                optionLabel: 'displayName',
                isLoading: this.segmentsIsLoading,
                options: this.segments,
                hasMore: this.segmentsHasMore,
                onLoadMore: (str: string) => this.onLoadMoreSegments(str),
                onSearch: (str: string) => this.onSearchSegments(str),
            },
            signalAnchorKey: {
                optionLabel: 'displayName',
                isLoading: this.signalsIsLoading,
                options: this.signals,
                hasMore: this.signalsHasMore,
                onLoadMore: (str: string) => this.onLoadMoreSignals(str),
                onSearch: (str: string) => this.onSearchSignals(str),
            },
            sourceAeAnchorKey: {
                optionLabel: 'displayName',
                isLoading: this.allocationElementsIsLoading,
                options: this.allocationElements,
                hasMore: this.allocationElementsHasMore,
                onLoadMore: (str: string) => this.onLoadMoreAllocationElements(str),
                onSearch: (str: string) => this.onSearchAllocationElements(str),
            },
            targetAeAnchorKey: {
                optionLabel: 'displayName',
                isLoading: this.allocationElementsIsLoading,
                options: this.allocationElements,
                hasMore: this.allocationElementsHasMore,
                onLoadMore: (str: string) => this.onLoadMoreAllocationElements(str),
                onSearch: (str: string) => this.onSearchAllocationElements(str),
            },
            fvBoxPortDataRelationAnchorKey: {
                optionLabel: 'displayName',
                isLoading: this.fvBoxPortDataRelationsIsLoading,
                options: this.fvBoxPortDataRelations,
                hasMore: this.fvBoxPortDataRelationsHasMore,
                onLoadMore: (str: string) => this.onLoadMoreFvBoxPortDataRelations(str),
                onSearch: (str: string) => this.onSearchFvBoxPortDataRelations(str),
            },
            valueDescriptionAnchorKey: {
                optionLabel: 'displayName',
                isLoading: this.valueDescriptionsIsLoading,
                options: this.valueDescriptions,
                hasMore: this.valueDescriptionsHasMore,
                onLoadMore: (str: string) => this.onLoadMoreValueDescriptions(str),
                onSearch: (str: string) => this.onSearchValueDescriptions(str),
            },
        };
        return (
            configs[fieldName] || {
                optionLabel: 'displayName',
                isLoading: false,
                options: [],
                hasMore: false,
                onLoadMore: () => {},
                onSearch: () => {},
            }
        );
    }

    public isSaveDisabled(): boolean {
        return !this.form || this.form.invalid || this.isCreating;
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public onClose(): void {
        if (this.hasUnsavedChanges) {
            this.confirmationDialogService.confirmUnsavedChanges().subscribe((confirmed) => {
                if (confirmed) {
                    this.closeModal();
                }
            });
        } else {
            this.closeModal();
        }
    }

    public onDropdownLoadMore(fieldName: string, searchString: string | Event): void {
        const str = typeof searchString === 'string' ? searchString : '';
        this.getDropdownConfig(fieldName).onLoadMore(str);
    }

    public onDropdownSearch(fieldName: string, searchString: string | Event): void {
        const str = typeof searchString === 'string' ? searchString : '';
        this.getDropdownConfig(fieldName).onSearch(str);
    }

    public onFieldBlur(fieldName: string): void {
        const control = this.form.get(fieldName);
        if (control && typeof control.value === 'string') {
            const trimmedValue = control.value.trim();
            if (trimmedValue !== control.value) {
                control.setValue(trimmedValue);
            }
        }
        if (fieldName === 'name') {
            control?.updateValueAndValidity();
        } else {
            control?.markAsDirty();
        }
    }

    public async onLoadMoreChangeRequests(str: string): Promise<void> {
        this.changeRequestsIsLoading = true;
        try {
            const newChangeRequests = await this.crService.get(
                this.changeRequestsLimit,
                this.changeRequests.length,
                str
            );
            this.changeRequests = [...this.changeRequests, ...newChangeRequests];
            this.changeRequestsHasMore = newChangeRequests.length >= this.changeRequestsLimit;
        } catch (e) {
            this.handleError('Failed to load change requests', e);
        } finally {
            this.changeRequestsIsLoading = false;
        }
    }

    public async onOpen(): Promise<void> {
        try {
            this.hasUnsavedChanges = false;
            await this.resetForm();

            if (!this.modalProps.anchor) {
                await this.setDefaultCr0();
            } else {
                await this.loadChangeRequests();
            }

            await this.loadDropdownData();
            this.isModalVisible = true;
        } catch (e) {
            this.handleError('Failed to open modal', e);
        }
    }

    public async onSearchChangeRequests(str: string): Promise<void> {
        this.changeRequestsIsLoading = true;
        try {
            const searchStr = str?.trim() || undefined;
            const newChangeRequest = await this.crService.getByName(searchStr);
            this.changeRequests = newChangeRequest;
            this.changeRequestsHasMore = false;
        } catch (e) {
            this.handleError('Failed to search change requests', e);
        } finally {
            this.changeRequestsIsLoading = false;
        }
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public onLoadMore = (type: keyof typeof this.serviceConfig, str: string) =>
        this.handleAnchorServiceOperation(type, str, true);
    public onLoadMoreAddresses = (str: string) => this.onLoadMore('addresses', str);
    public onLoadMoreAllocationElements = (str: string) =>
        this.onLoadMore('allocationElements', str);
    public onLoadMoreComponentCodes = (str: string) => this.onLoadMore('componentCodes', str);
    public onLoadMoreComponents = (str: string) => this.onLoadMore('components', str);
    public onLoadMoreFunctionCategories = (str: string) =>
        this.onLoadMore('functionCategories', str);
    public onLoadMoreFvBoxPortDataRelations = (str: string) =>
        this.onLoadMore('fvBoxPortDataRelations', str);
    public onLoadMoreSegments = (str: string) => this.onLoadMore('segments', str);
    public onLoadMoreSignals = (str: string) => this.onLoadMore('signals', str);
    public onLoadMoreUseCases = (str: string) => this.onLoadMore('useCases', str);
    public onLoadMoreUserFunctions = (str: string) => this.onLoadMore('userFunctions', str);
    public onLoadMoreValueDescriptions = (str: string) => this.onLoadMore('valueDescriptions', str);
    public onSearch = (type: keyof typeof this.serviceConfig, str: string) =>
        this.handleAnchorServiceOperation(type, str);
    public onSearchAddresses = (str: string) => this.onSearch('addresses', str);
    public onSearchAllocationElements = (str: string) => this.onSearch('allocationElements', str);
    public onSearchComponentCodes = (str: string) => this.onSearch('componentCodes', str);
    public onSearchComponents = (str: string) => this.onSearch('components', str);
    public onSearchFunctionCategories = (str: string) => this.onSearch('functionCategories', str);
    public onSearchFvBoxPortDataRelations = (str: string) =>
        this.onSearch('fvBoxPortDataRelations', str);
    public onSearchSegments = (str: string) => this.onSearch('segments', str);
    public onSearchSignals = (str: string) => this.onSearch('signals', str);
    public onSearchUseCases = (str: string) => this.onSearch('useCases', str);
    public onSearchUserFunctions = (str: string) => this.onSearch('userFunctions', str);
    public onSearchValueDescriptions = (str: string) => this.onSearch('valueDescriptions', str);

    public preventNegativeInput(event: KeyboardEvent, field: ModalFieldConfig): void {
        if (field.type === 'number' && event.key === '-') {
            event.preventDefault();
        }
    }

    public async resetForm(): Promise<void> {
        if (!this.modalProps) return;

        const allFields = [
            ...(this.modalProps.anchorFields || []),
            ...(this.modalProps.versionFields || []),
        ];
        const latestVersion = getLatestVersion(this.modalProps.anchor);
        const anchorFieldNames = new Set((this.modalProps.anchorFields || []).map((f) => f.field));

        const defaults = new Map<string, string | number | boolean>();
        if (!this.modalProps.anchor) {
            await Promise.all(
                allFields.map(async (field) => {
                    if (!field.getDefaultValue) return;
                    try {
                        defaults.set(field.field, await field.getDefaultValue());
                    } catch (e) {
                        this.handleError(`Failed to suggest ${field.label}`, e);
                    }
                })
            );
        }

        const controls = allFields.reduce(
            (acc, field) => {
                let value = getFieldValue(field, this.modalProps, latestVersion);

                value = applyDefaultValue(field, value);
                if (defaults.has(field.field)) value = defaults.get(field.field);

                const validators = [];
                if (field.required) validators.push(trimmedRequiredValidator);
                if (field.field === 'name' && this.modalProps.validateNameFormat === true) {
                    validators.push(nameFormatValidator(!this.modalProps.anchor));
                }
                if (field.validator) validators.push(field.validator);
                if (field.type === 'number') {
                    if (field.min !== undefined) validators.push(Validators.min(field.min));
                }

                const asyncValidators =
                    field.uniqueCheck && !this.modalProps.anchor
                        ? [this.uniqueFieldValidator(field)]
                        : [];

                // Create FormControl
                const control = new FormControl(value, {
                    validators: validators,
                    asyncValidators: asyncValidators,
                    updateOn: 'change',
                });

                // Disable if field is readonly
                if (
                    field.disabled ||
                    (anchorFieldNames.has(field.field) && this.modalProps.anchor)
                ) {
                    control.disable();
                }
                acc[field.field] = control;
                return acc;
            },
            {} as { [key: string]: FormControl }
        );

        this.form = new FormGroup(controls);

        Object.keys(this.form.controls).forEach((fieldName) => {
            const control = this.form.get(fieldName);
            this.form.get(fieldName)?.valueChanges.subscribe(() => {
                if (control?.hasError('conflict')) {
                    control.setErrors(null);
                }
            });
            const fieldConfig = allFields.find((f) => f.field === fieldName);
            if (fieldConfig?.uniqueCheck) {
                control?.setAsyncValidators(this.uniqueFieldValidator(fieldConfig));
            }
        });

        this.setupFormChangeTracking();
    }

    public async save(): Promise<T | V> {
        if (this.form.invalid) {
            markFormGroupTouched(this.form);
            throw new Error('Form is invalid');
        }

        this.isCreating = true;
        try {
            const result =
                this.modalProps.anchor && !this.hasDirtyVersionFields()
                    ? (this.modalProps.anchor as T)
                    : this.modalProps.anchor
                      ? await this.createVersion()
                      : await this.createAnchor();
            await this.modalProps.afterSave?.(result);
            await this.modalProps.onSave?.(result);
            this.hasUnsavedChanges = false;

            if (!this.modalProps.anchor) {
                await this.resetForm();
            }

            return result;
        } catch (error: unknown) {
            if (typeof error === 'object' && error !== null && 'status' in error) {
                const err = error as { status?: number; error?: { message?: string } };

                if (
                    err.status === 409 &&
                    this.modalProps.title.toLowerCase() === 'scenario' &&
                    !this.modalProps.anchor
                ) {
                    const message = err.error?.message || 'Scenario already exists';
                    this.handleError(message);
                    this.form.get('scenarioId')?.setErrors({ conflict: message });
                } else {
                    this.handleError('Failed to save', error);
                }
            } else {
                this.handleError('Failed to save', error);
            }
            throw error;
        } finally {
            this.isCreating = false;
        }
    }

    public async saveAndClose(): Promise<void> {
        await this.save();
        this.isModalVisible = false;
    }

    protected async createAnchor(): Promise<T> {
        const formValue = this.form.getRawValue();
        const anchorFieldNames = (this.modalProps.anchorFields || []).map((f) => f.field);
        const versionFieldNames = (this.modalProps.versionFields || []).map((f) => f.field);

        const { anchorData, versionData } = processFormData(
            formValue,
            anchorFieldNames,
            versionFieldNames
        );
        const anchor = { ...anchorData, allVersions: [versionData] };

        try {
            const result = await (
                this.modalProps.service as {
                    createAnchors: (data: unknown[]) => Promise<unknown[]>;
                }
            ).createAnchors([anchor]);
            const createdItem = (result as unknown[])[0] as T;
            const itemName =
                (createdItem as Record<string, unknown>)?.['displayName'] ||
                (createdItem as Record<string, unknown>)?.['name'] ||
                'Item';
            this.showSuccessMessage(`${this.modalProps.title} '${itemName}' created successfully`);

            if (!this.modalProps.skipPendingChangesCountRefresh) {
                await refreshPendingChangesCount(this.store, this.pendingChangesService);
            }

            return createdItem;
        } catch (error) {
            this.handleError('Failed to create anchor', error);
            throw error;
        }
    }

    protected async createVersion(): Promise<V> {
        const rawFormValue = this.form.getRawValue() as Record<string, unknown>;
        const versionFieldNames = new Set(
            (this.modalProps.versionFields || []).map((field) => field.field)
        );
        const formValue = Object.keys(rawFormValue).reduce(
            (acc, key) => {
                if (versionFieldNames.has(key)) {
                    acc[key] = parseHexFieldValue(key, rawFormValue[key]);
                }
                return acc;
            },
            {} as Record<string, unknown>
        );
        formValue['anchorKey'] = (this.modalProps.anchor as Record<string, unknown>)['key'];
        formValue['logText'] = '';

        try {
            const result = await (
                this.modalProps.service as {
                    createVersions: (data: unknown[]) => Promise<unknown[]>;
                }
            ).createVersions([formValue]);
            const createdVersion = (result as unknown[])[0] as V;
            this.updateModalAnchorVersion(createdVersion);
            const itemName = (createdVersion as Record<string, unknown>)?.['name'] || 'Version';
            this.showSuccessMessage(
                `${this.modalProps.title} version '${itemName}' created successfully`
            );

            await refreshPendingChangesCount(this.store, this.pendingChangesService);
            return createdVersion;
        } catch (error) {
            this.handleError('Failed to create version', error);
            throw error;
        }
    }

    protected handleError(message: string, error?: unknown): void {
        this.notificationService.handleError(message, error);
    }

    protected setupFormChangeTracking(): void {
        this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.hasUnsavedChanges = this.form.dirty;
        });
    }

    protected showSuccessMessage(message: string): void {
        this.notificationService.showSuccess(message);
    }

    private checkFieldConflict(field: ModalFieldConfig, value: string): Observable<boolean> {
        if (!this.modalProps?.service) return of(false);

        const anchorType = this.modalProps.title.toLowerCase(); // generic anchor type
        const service = this.modalProps.service as AnchorServiceLike;

        if (!service.checkFieldUnique) return of(false);

        return service.checkFieldUnique(field.field, value, anchorType);
    }

    private closeModal(): void {
        this.isModalVisible = false;
        this.hasUnsavedChanges = false;
        this.isCreating = false;
        this.modalProps.onClose?.();
    }

    private async ensureAnchorValuesInDropdowns(): Promise<void> {
        const anchorFields = this.modalProps.anchorFields || [];
        const lazyDropdownFields = anchorFields.filter((field) => field.type === 'lazy-dropdown');

        const loadPromises = lazyDropdownFields.map(async (field) => {
            const anchorValue = ((this.modalProps.anchor as Record<string, unknown>)?.[
                field.field
            ] ?? field.defaultValue) as number | undefined;
            if (typeof anchorValue === 'number') {
                await this.loadSpecificDropdownItem(field.field, anchorValue);
            }
        });

        await Promise.all(loadPromises);
    }

    private async ensureVersionFieldValuesInDropdowns(): Promise<void> {
        const versionFields = this.modalProps.versionFields || [];
        const lazyDropdownFields = versionFields.filter((field) => field.type === 'lazy-dropdown');
        const latestVersion = getLatestVersion(this.modalProps.anchor);

        const loadPromises = lazyDropdownFields.map(async (field) => {
            const fieldValue = getFieldValue(field, this.modalProps, latestVersion);

            if (fieldValue !== null && fieldValue !== '' && fieldValue !== undefined) {
                if (field.field === 'changeRequestKey') {
                    const key = fieldValue as number;
                    if (!this.changeRequests.some((cr) => cr.key === key)) {
                        const cr = await this.crService.getByKey(key);
                        if (cr) this.changeRequests = [cr, ...this.changeRequests];
                    }
                } else {
                    await this.loadSpecificDropdownItem(field.field, fieldValue as number);
                }
            }
        });

        await Promise.all(loadPromises);
    }

    private async handleAnchorServiceOperation(
        type: keyof typeof this.serviceConfig,
        str: string,
        isLoadMore = false
    ): Promise<void> {
        const config = this.serviceConfig[type];
        const propHandler = this.propertyMap.get(config.prop);
        if (!propHandler) return;

        propHandler.setLoading(true);

        try {
            const currentArray = propHandler.array();
            const limit = config.limit as number;
            const filter: AnchorAndVersionBaseFilter = {
                qLimit: limit,
                qOffset: isLoadMore ? currentArray.length : 0,
                qDisplayName: str?.trim() || undefined,
            };
            const newItems = await (
                config.service as {
                    getAnchorsWithValidVersion: (
                        filter: AnchorAndVersionBaseFilter
                    ) => Promise<unknown[]>;
                }
            ).getAnchorsWithValidVersion(filter);
            propHandler.setArray(isLoadMore ? [...currentArray, ...newItems] : newItems);
            propHandler.setHasMore(
                isLoadMore ? newItems.length >= limit : newItems.length >= limit
            );
        } catch (e) {
            this.handleError(`Failed to ${isLoadMore ? 'load' : 'search'} ${type}`, e);
        } finally {
            propHandler.setLoading(false);
        }
    }

    private hasDirtyVersionFields(): boolean {
        return (this.modalProps.versionFields || []).some(
            (field) => this.form.get(field.field)?.dirty
        );
    }

    private async loadChangeRequests(): Promise<void> {
        this.changeRequestsIsLoading = true;
        try {
            this.changeRequests = await this.crService.get(this.changeRequestsLimit, 0);
        } catch (e) {
            this.changeRequests = [];
            this.handleError('Failed to load change requests', e);
        } finally {
            this.changeRequestsIsLoading = false;
        }
    }

    private async loadDropdownData(): Promise<void> {
        try {
            const allFields = [
                ...(this.modalProps?.anchorFields || []),
                ...(this.modalProps?.versionFields || []),
            ];

            const editableDropdownFields = allFields.filter((field) => {
                const isAnchorField = (this.modalProps.anchorFields || []).some(
                    (f) => f.field === field.field
                );
                const isReadonly = (isAnchorField && this.modalProps?.anchor) || field.disabled;
                return (field.type === 'dropdown' || field.type === 'lazy-dropdown') && !isReadonly;
            });

            const loadersToRun = editableDropdownFields
                .map(
                    (field) =>
                        this.dropdownLoaders[field.field as keyof typeof this.dropdownLoaders]
                )
                .filter(Boolean);

            const uniqueLoaders = [...new Set(loadersToRun)];
            await Promise.all(uniqueLoaders.map((loader) => loader()));

            await this.ensureAnchorValuesInDropdowns();

            await this.ensureVersionFieldValuesInDropdowns();
        } catch (e) {
            this.handleError('Failed to load dropdown data', e);
        }
    }

    private async loadSpecificDropdownItem(
        fieldName: string,
        key: number | undefined
    ): Promise<void> {
        if (key === undefined) return;
        const serviceType = FIELD_TO_SERVICE_MAP[fieldName as keyof typeof FIELD_TO_SERVICE_MAP];
        const config = this.serviceConfig[serviceType as keyof typeof this.serviceConfig];
        const propHandler = this.propertyMap.get(config?.prop);

        if (
            !config?.service ||
            !propHandler ||
            !('getAnchorsWithValidVersion' in (config.service as object))
        ) {
            return;
        }

        try {
            const [item] = await (
                config.service as {
                    getAnchorsWithValidVersion: (
                        filter: AnchorAndVersionBaseFilter
                    ) => Promise<unknown[]>;
                }
            ).getAnchorsWithValidVersion({ qLimit: 1, qOffset: 0, aKey: key });

            if (item) {
                propHandler.setArray([item, ...propHandler.array()]);
            }
        } catch (e) {
            this.handleError(`Failed to load specific ${fieldName} item`, e);
        }
    }

    private async setDefaultCr0(): Promise<void> {
        try {
            const results = await this.crService.getByName('CR 0');
            const cr0 = results[0];
            if (cr0) {
                this.changeRequests = [cr0];
                this.form.get('changeRequestKey')?.setValue(cr0.key);
                this.form.get('changeRequestKey')?.disable();
            }
        } catch (e) {
            this.handleError('Failed to load CR 0', e);
        }
    }

    private uniqueFieldValidator(field: ModalFieldConfig): AsyncValidatorFn {
        return (control: AbstractControl) => {
            if (!control.value) return of(null);

            return of(control.value).pipe(
                debounceTime(500), // wait for typing
                distinctUntilChanged(),
                switchMap((value) => this.checkFieldConflict(field, value)),
                map((exists: boolean) =>
                    exists ? { conflict: `${field.label} "${control.value}" already exists` } : null
                ),
                first()
            );
        };
    }

    private updateModalAnchorVersion(createdVersion: V): void {
        const anchor = this.modalProps.anchor as Record<string, unknown>;
        anchor['validVersion'] = createdVersion;

        const allVersions = Array.isArray(anchor['allVersions'])
            ? (anchor['allVersions'] as V[])
            : [];
        const createdVersionKey = (createdVersion as Record<string, unknown>)['key'];
        const existingVersionIndex = allVersions.findIndex(
            (version) => (version as Record<string, unknown>)['key'] === createdVersionKey
        );

        anchor['allVersions'] =
            existingVersionIndex === -1
                ? [...allVersions, createdVersion]
                : allVersions.map((version, index) =>
                      index === existingVersionIndex ? createdVersion : version
                  );
    }
}
